import type FlagStatus from 'owa-service/lib/contract/FlagStatus';
import getConversationProperty from './getConversationProperty';
import getCurrentSelectedTableView from '../utils/getSelectedTableView';
import getItemProperty from './getItemProperty';
import isItemView from '../utils/isItemView';
import type SingleRecipientType from 'owa-service/lib/contract/SingleRecipientType';
import type TableView from '../store/schema/TableView';
import { type MailListDataPropertyEnum } from './MailListDataPropertyEnum';

/**
 * Get the specified property from the row
 * @param rowId conversation/item id
 * @param tableView the table that contains the row data
 * @param propertyType to retrieve on the row data
 */
export const getRowDataProperty = (
    rowId: string,
    tableView: TableView,
    propertyType: MailListDataPropertyEnum
) => {
    if (isItemView(tableView)) {
        return getItemProperty(rowId, propertyType);
    }

    return getConversationProperty(rowId, tableView, propertyType);
};

/**
 * Get the lastDeliveryTimeStamp of a row
 * @param rowId rowId of row
 * @param tableView the table that contains the row data
 * @return the lastDeliveryTimeStamp
 */
export const getLastDeliveryTimeStamp = (
    rowId: string,
    tableView: TableView = getCurrentSelectedTableView()
) => getRowDataProperty(rowId, tableView, 0) as string;

/**
 * Get the LastDeliveryOrRenewTimeStamp for a row
 * @return the lastDeliveryTimeStamp
 */
export const getLastDeliveryOrRenewTimeStamp = (
    rowId: string,
    tableView: TableView = getCurrentSelectedTableView()
) => getRowDataProperty(rowId, tableView, 1) as string;

export const getSubject = (rowId: string, tableView: TableView = getCurrentSelectedTableView()) =>
    getRowDataProperty(rowId, tableView, 3) as string;

export const getParentFolderId = (
    rowId: string,
    tableView: TableView = getCurrentSelectedTableView()
) => getRowDataProperty(rowId, tableView, 4) as string;

export const getUniqueRecipients = (
    rowId: string,
    tableView: TableView = getCurrentSelectedTableView()
) => getRowDataProperty(rowId, tableView, 5) as string[];

export const getUniqueSenders = (
    rowId: string,
    tableView: TableView = getCurrentSelectedTableView()
) => getRowDataProperty(rowId, tableView, 6) as string[];

export const getLastSender = (
    rowId: string,
    tableView: TableView = getCurrentSelectedTableView()
) => getRowDataProperty(rowId, tableView, 7) as SingleRecipientType;

export const getUnreadCount = (
    rowId: string,
    tableView: TableView = getCurrentSelectedTableView()
) => getRowDataProperty(rowId, tableView, 8) as number;

export const getPreviewText = (
    rowId: string,
    tableView: TableView = getCurrentSelectedTableView()
) => getRowDataProperty(rowId, tableView, 9) as string;

export function getIsPinned(
    rowId: string,
    tableView: TableView = getCurrentSelectedTableView()
): boolean {
    return getRowDataProperty(rowId, tableView, 10) as boolean;
}

export function getFlagStatus(
    rowId: string,
    tableView: TableView = getCurrentSelectedTableView()
): FlagStatus {
    return getRowDataProperty(rowId, tableView, 11) as FlagStatus;
}

/**
 * Get the LastModifiedTime for a row
 */
export function getLastModifiedTime(rowId: string, tableView: TableView) {
    return getRowDataProperty(rowId, tableView, 2) as string;
}

/**
 * Get the shouldShowRSVP property for a row
 */
export const getShouldShowRSVP = (
    rowId: string,
    tableView: TableView = getCurrentSelectedTableView()
) => !!getRowDataProperty(rowId, tableView, 12);

/**
 * Get the rsvpUnitAriaDetails property for a row
 */
export const getRsvpUnitAriaDetailsForMailListRow = (
    rowId: string,
    tableView: TableView = getCurrentSelectedTableView()
) => getRowDataProperty(rowId, tableView, 13) as string;

/**
 * Get the ItemIds for a row
 */
export const getItemIds = (rowId: string, tableView: TableView = getCurrentSelectedTableView()) =>
    getRowDataProperty(rowId, tableView, 14) as string[];

/**
 * Get the ConversationId for a row
 **/
export const getConversationId = (
    rowId: string,
    tableView: TableView = getCurrentSelectedTableView()
) => getRowDataProperty(rowId, tableView, 15) as string;

/**
 * Get the InferenceClassification property for a row
 */
export const getInferenceClassification = (
    rowId: string,
    tableView: TableView = getCurrentSelectedTableView()
) => getRowDataProperty(rowId, tableView, 16) as string;
